import { useParams } from '@tanstack/react-router';
import { SparklesIcon } from 'lucide-react';

import { EyebrowLabel } from '@/shared/components/EyebrowLabel';
import { useLlmProfiles } from '@/features/llm-profiles/hooks/useLlmProfiles';
import { ProfileSummary } from '@/features/llm-profiles/components/ProfileSummary';
import { AiModelsLink } from '@/features/ai-models/components/AiModelsLink';

/**
 * What an AI step runs on, for anyone reading the diagram (spec 214 US3).
 *
 * It used to print the step's profile reference under "Model" — an internal
 * name, not a model. It now reads the same profile list the builder does and
 * shows the same card, or says plainly that the profile is not there.
 */
export function StepProfile({ slug }: { slug: string }) {
  const { entries, isPending, loadFailed } = useLlmProfiles();
  const { workspaceId } = useParams({ strict: false });
  const profile = entries?.find((entry) => entry.slug === slug);
  return (
    <div className="flex flex-col gap-1">
      <EyebrowLabel icon={SparklesIcon}>AI profile</EyebrowLabel>
      {profile ? (
        <>
          <ProfileSummary profile={profile} variant="card" />
          {workspaceId && <AiModelsLink workspaceId={workspaceId} />}
        </>
      ) : isPending ? null : (
        <p className="text-muted-foreground text-sm">
          {loadFailed
            ? "The step's profile could not be loaded."
            : `The profile ${slug} is not available.`}
        </p>
      )}
    </div>
  );
}
